import { Component, OnDestroy, OnInit } from '@angular/core';

import { ActionCableService } from '../../../services/common/action-cable.service';
import { AuthService } from '../../../services/common/auth.service';
import { NotyService } from '../../../services/common/noty.service';
import { DocumentInboxService } from '../../../services/documents/document-inbox.service';

@Component({
  selector: 'app-document-inbox',
  templateUrl: './document-inbox.component.html',
  styleUrls: ['./document-inbox.component.scss']
})
export class DocumentInboxComponent implements OnInit, OnDestroy {
  matters: Array<any> = [];
  counts: any = { unreviewed: 0, reviewed: 0, all: 0 };
  pagination: any = { page: 1, per_page: 25, total_matters: 0, total_documents: 0 };
  filters: any = { days: 7, review_status: 'unreviewed', query: '' };
  expandedMatterIds: Set<number> = new Set<number>();
  loading: boolean = false;
  updatingDocumentIds: Set<number> = new Set<number>();
  updatingMatterIds: Set<number> = new Set<number>();
  private channelSubscription: any;
  private refreshTimer: any;
  private fallbackRefreshTimer: any;
  private componentDestroyed: boolean = false;

  constructor(
    private documentInboxService: DocumentInboxService,
    private notyService: NotyService,
    public authService: AuthService,
    private actionCableService: ActionCableService
  ) {}

  ngOnInit(): void {
    this.load();
    this.subscribeForChanges();
    this.scheduleFallbackRefresh();
  }

  ngOnDestroy(): void {
    this.componentDestroyed = true;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    if (this.fallbackRefreshTimer) clearTimeout(this.fallbackRefreshTimer);
    if (this.channelSubscription) this.channelSubscription.unsubscribe();
  }

  load(page: number = 1): void {
    this.loading = true;
    this.pagination.page = page;
    this.documentInboxService.index({
      days: this.filters.days,
      review_status: this.filters.review_status,
      query: this.filters.query,
      page: this.pagination.page,
      per_page: this.pagination.per_page
    }).then((response: any) => {
      this.matters = response.matters || [];
      this.counts = response.counts || this.counts;
      this.pagination = response.pagination || this.pagination;
      if (this.matters.length === 1) this.expandedMatterIds.add(this.matters[0].matter_id);
      this.loading = false;
    }).catch(() => {
      this.loading = false;
      this.notyService.notify('The Document Inbox could not be loaded.', 'error');
    });
  }

  applyFilters(): void {
    this.expandedMatterIds.clear();
    this.load(1);
  }

  setReviewStatus(status: string): void {
    this.filters.review_status = status;
    this.applyFilters();
  }

  toggleMatter(matterId: number): void {
    if (this.expandedMatterIds.has(matterId)) this.expandedMatterIds.delete(matterId);
    else this.expandedMatterIds.add(matterId);
  }

  isExpanded(matterId: number): boolean {
    return this.expandedMatterIds.has(matterId);
  }

  markReviewed(document: any, reviewed: boolean): void {
    if (this.updatingDocumentIds.has(document.document_id)) return;

    this.updatingDocumentIds.add(document.document_id);
    const request = reviewed
      ? this.documentInboxService.markReviewed(document.document_id)
      : this.documentInboxService.markUnreviewed(document.document_id);

    request.then(() => {
      this.updatingDocumentIds.delete(document.document_id);
      this.notyService.notify(reviewed ? 'Document marked reviewed.' : 'Document returned to unreviewed.', 'success');
      this.load(this.pagination.page);
    }).catch(() => {
      this.updatingDocumentIds.delete(document.document_id);
      this.notyService.notify('The review status could not be updated.', 'error');
    });
  }

  isUpdating(documentId: number): boolean {
    return this.updatingDocumentIds.has(documentId);
  }

  reviewAndHide(document: any): void {
    if (!document.document_id || this.updatingDocumentIds.has(document.document_id)) return;

    this.updatingDocumentIds.add(document.document_id);
    this.documentInboxService.reviewAndHide(document.document_id).then(() => {
      this.updatingDocumentIds.delete(document.document_id);
      this.notyService.notify('Document reviewed and hidden.', 'success');
      this.load(this.pagination.page);
    }).catch(() => {
      this.updatingDocumentIds.delete(document.document_id);
      this.notyService.notify('The document could not be hidden.', 'error');
    });
  }

  reviewAll(matter: any): void {
    if (this.updatingMatterIds.has(matter.matter_id) || !matter.unreviewed_document_ids?.length) return;

    const documentCount = matter.unreviewed_document_ids.length;
    const noun = documentCount === 1 ? 'document' : 'documents';
    if (!window.confirm(`Are you sure you want to mark all ${documentCount} unreviewed ${noun} in this matter as reviewed?`)) return;

    this.updatingMatterIds.add(matter.matter_id);
    this.documentInboxService.reviewAll(matter.matter_id, matter.unreviewed_document_ids).then(() => {
      this.updatingMatterIds.delete(matter.matter_id);
      this.notyService.notify(`All ${documentCount} ${noun} were marked reviewed.`, 'success');
      this.load(this.pagination.page);
    }).catch(() => {
      this.updatingMatterIds.delete(matter.matter_id);
      this.notyService.notify('The matter documents could not be marked reviewed.', 'error');
    });
  }

  hideAll(matter: any): void {
    if (this.updatingMatterIds.has(matter.matter_id) || !matter.hideable_document_ids?.length) return;

    this.updatingMatterIds.add(matter.matter_id);
    this.documentInboxService.hideAll(matter.matter_id, matter.hideable_document_ids).then(() => {
      this.updatingMatterIds.delete(matter.matter_id);
      this.notyService.notify('All visible documents were reviewed and hidden.', 'success');
      this.load(this.pagination.page);
    }).catch(() => {
      this.updatingMatterIds.delete(matter.matter_id);
      this.notyService.notify('The matter documents could not be hidden.', 'error');
    });
  }

  unhideAll(matter: any): void {
    if (this.updatingMatterIds.has(matter.matter_id)) return;

    this.updatingMatterIds.add(matter.matter_id);
    this.documentInboxService.unhideAll(matter.matter_id).then(() => {
      this.updatingMatterIds.delete(matter.matter_id);
      this.filters.review_status = 'all';
      this.notyService.notify('All hidden documents for this matter are visible again.', 'success');
      this.load(this.pagination.page);
    }).catch(() => {
      this.updatingMatterIds.delete(matter.matter_id);
      this.notyService.notify('The matter documents could not be unhidden.', 'error');
    });
  }

  isMatterUpdating(matterId: number): boolean {
    return this.updatingMatterIds.has(matterId);
  }

  authenticatedUrl(url: string): string {
    return this.authService.getAuthenticatedDownloadUrl(url);
  }

  activateIntranet(intranetId: number): void {
    if (!intranetId) return;

    const value = String(intranetId);
    window.sessionStorage.setItem('intranetId', value);
    window.localStorage.setItem('intranetId', value);
    document.cookie = `intranetId=${encodeURIComponent(value)}; path=/`;
  }

  pageChanged(event: any): void {
    this.load(event.page);
  }

  private subscribeForChanges(): void {
    this.actionCableService.cable().then((cable: any) => {
      if (this.componentDestroyed) return;

      this.channelSubscription = cable.subscriptions.create(
        { channel: 'DocumentInboxChannel' },
        {
          connected: () => this.scheduleRefresh(),
          received: (data: any) => {
            if (data && data.action === 'document_inbox_changed') this.scheduleRefresh();
          }
        }
      );
    });
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      if (!this.componentDestroyed) this.load(this.pagination.page);
    }, 250);
  }

  private scheduleFallbackRefresh(): void {
    if (this.fallbackRefreshTimer) clearTimeout(this.fallbackRefreshTimer);
    this.fallbackRefreshTimer = setTimeout(() => {
      this.fallbackRefreshTimer = null;
      if (this.componentDestroyed) return;

      this.load(this.pagination.page);
      this.scheduleFallbackRefresh();
    }, 60000);
  }
}
